import qs from 'qs';
import { get } from 'svelte/store';
import { format } from 'svelte-i18n';
import { replace } from 'svelte-spa-router';

import { toast } from '@Shelly/shelly-design-system';

import { user_data } from 'src/stores/account/user';
import { profiles } from 'src/stores/features/profiles';
import { currentUnitId, getSubIdForHome, homes, refreshHomes } from 'src/stores/account/home';

import { switchUnit } from 'src/services/auth_v2';
import { get_user_settings } from 'src/services/http';
import { onChangeProfile } from 'src/components/Profiles/profiles_helpers';
import { getLandingHref } from 'src/helpers/main_menu';
import { normalizeEmail } from 'src/helpers/email_helpers';

import type { Home } from 'src/types/Home';
import type { AvailableSubUnits, AvailableUnits } from 'src/types/responses/usersAuthResponse';

/**
 * Payload forwarded by the native layer when a push notification is opened.
 * `email` identifies the account the event belongs to, `uid` the home (unit_id).
 * See FE-1860 (contract) / FE-1864 (web validation + routing).
 */
export type NotificationDeepLink = {
	email: string;
	uid: string;
};

/**
 * Result of attempting to handle a native deep-link (`go_to` bridge call).
 * - `ignored`: the string is not a `uid` deep-link — the caller should fall
 *   through to its other `go_to` routes.
 * - `routed`: fully handled here (landed on the home, or an error was shown) —
 *   the caller should stop.
 * - `context-ready`: the correct account + home is now active and the caller
 *   should open the room/device target carried by the link.
 */
export type DeepLinkOutcome = 'ignored' | 'routed' | 'context-ready';

/**
 * Parse the native notification deep-link string into `{ email, uid }`.
 *
 * Android sends `?email=<email>&uid=<uid>` and iOS `/?email=<email>&uid=<uid>`,
 * so we always read the query part after the first `?`. Returns `null` when the
 * string is not a notification deep-link (no `uid`), letting the caller fall
 * through to the other `go_to` routes.
 */
export const parseNotificationDeepLink = (str: string): NotificationDeepLink | null => {
	if (!str || !str.includes('uid=')) return null;

	const queryPart = str.split('?')[1];
	if (!queryPart) return null;

	const params = qs.parse(queryPart);
	const uid = params.uid ? String(params.uid) : '';
	if (!uid) return null;

	return {
		email: params.email ? normalizeEmail(String(params.email)) : '',
		uid,
	};
};

/** Find a home (owned or shared) in the active account's cached list by its `uid`. */
const findHomeByUid = (uid: string): Home | undefined => get(homes).list.find((home) => home.id === uid);

/** Land on the active home after the correct account/unit context is in place. */
const routeToActiveHome = (): void => {
	replace(getLandingHref());
};

/**
 * Refresh the now-active account's profile (avatar/title) after a switch.
 * When a notification is handled during app launch, the previous account's
 * initial load may have already set `loadingData = true`, so the switch's
 * `pullDataWithLoading()` is a no-op and `get_user_settings()` never runs for
 * the new account — leaving the profile chip showing the previous account's
 * avatar. Refreshing settings here is independent of that gate and keeps the
 * active profile in sync with the session we just switched into.
 */
const refreshActiveProfile = async (): Promise<void> => {
	try {
		await get_user_settings();
	} catch (error) {
		console.error('[NOTIFICATION_DEEPLINK] Failed to refresh profile after switch:', error);
	}
};

/**
 * Surface the "home from the notification can't be opened" outcome (FE-1864
 * outcome 3). Centralised so the error ownership decision from FE-1860
 * (native-suppressed vs web toast) is a single switch to flip.
 */
const showUnresolvedHomeError = (): void => {
	toast.open({
		type: 'danger',
		title: get(format)('error_notification_home_unavailable', {
			default: "We couldn't open the home from this notification.",
		}),
	});
};

/**
 * Settle a successful switch: when the link targets a room/device, hand control
 * back to the caller so it can open the entity; otherwise land on the home.
 */
const settleSwitch = (hasEntityTarget: boolean): DeepLinkOutcome => {
	if (hasEntityTarget) return 'context-ready';
	routeToActiveHome();
	return 'routed';
};

/**
 * Resolve a `uid` that belongs to the currently active account: settle directly
 * when it is already the active home, otherwise switch home first.
 */
const routeWithinActiveAccount = async (
	uid: string,
	hasEntityTarget: boolean,
): Promise<DeepLinkOutcome> => {
	if (uid === get(currentUnitId)) {
		return settleSwitch(hasEntityTarget);
	}

	let home = findHomeByUid(uid);
	if (!home) {
		// Cache may be stale (e.g. a home added on another device). Refetch once
		// before deciding the uid is genuinely unknown.
		await refreshHomes();
		home = findHomeByUid(uid);
	}

	if (!home) {
		showUnresolvedHomeError();
		return 'routed';
	}

	if (home.isActive) {
		return settleSwitch(hasEntityTarget);
	}

	// Sub-unit id for a shared home, as the string `switchUnit` expects.
	const sub = getSubIdForHome(home.id);
	await switchUnit(home.id, sub != null ? String(sub) : undefined);
	return settleSwitch(hasEntityTarget);
};

/**
 * Locate a `uid` inside an account's available homes and resolve the `subId`
 * required to log into it. Owned homes need no `subId`; shared homes carry it
 * on the sub-unit entry (mirrors `UnitSelectionModal.getSubId`).
 *
 * @returns the (possibly undefined) `subId` wrapped in an object, or `null`
 *   when the `uid` belongs to neither owned nor shared homes.
 */
const resolveUnitFromAvailable = (
	uid: string,
	availableUnits: AvailableUnits | undefined,
	availableSubUnits: AvailableSubUnits | undefined,
): { subId?: string } | null => {
	if (availableUnits && uid in availableUnits) {
		return { subId: undefined };
	}

	const subEntry = availableSubUnits?.[uid];
	if (subEntry != null) {
		const sub = typeof subEntry === 'object' ? subEntry.sub : undefined;
		return { subId: sub != null ? String(sub) : undefined };
	}

	return null;
};

/**
 * Resolve a `uid` that belongs to another logged-in account (multi-account).
 *
 * Mirrors the profile-switch → unit-selection flow used by `DropdownProfile`:
 * 1. Log into the account without a unit to learn its owned/shared homes.
 * 2. Log into the specific `uid` with the correct `subId` for shared homes.
 *
 * Passing the `subId` is what fixes shared (sub-unit) homes: logging in with
 * only the unit id returns the wrong session, so the homes list ends up stale
 * (FE-1864 follow-up).
 */
const switchAccountAndRoute = async (
	profile: { email: string; id?: number; sub?: number },
	uid: string,
	hasEntityTarget: boolean,
): Promise<DeepLinkOutcome> => {
	const profileId = profile.id ?? 0;

	// Refresh the now-active profile chip, then settle (open entity or land home).
	const settle = async (): Promise<DeepLinkOutcome> => {
		await refreshActiveProfile();
		return settleSwitch(hasEntityTarget);
	};

	// Step 1 — establish the account session without committing to a unit.
	const pending = await onChangeProfile(profile.email, profileId, profile.sub);

	// Single-home account: already fully logged in, no selection needed.
	if (!pending || !('multipleUnits' in pending)) {
		if (uid === get(currentUnitId)) {
			return settle();
		}
		showUnresolvedHomeError();
		return 'routed';
	}

	// Step 2 — resolve the uid against this account's homes and log into it.
	// Resolve `subId` from the login pending result, which is scoped to the
	// just-authenticated target account and avoids the `refreshHomes` in-flight
	// race that can leave `user_data.available_*` on the wrong account.
	const { availableUnits, availableSubUnits } = pending;
	const target = resolveUnitFromAvailable(uid, availableUnits, availableSubUnits);
	if (!target) {
		showUnresolvedHomeError();
		return 'routed';
	}

	const switched = await onChangeProfile(
		profile.email,
		profileId,
		profile.sub,
		uid,
		target.subId,
		availableUnits,
	);

	if (switched && 'multipleUnits' in switched) {
		showUnresolvedHomeError();
		return 'routed';
	}

	return settle();
};

/**
 * Entry point for native `uid` deep-links (`go_to` bridge call), covering both
 * push notifications and home-screen widget links.
 *
 * Validates the `uid` against the logged-in session(s) and switches context
 * accordingly:
 * 1. `uid` is the active home → no switch needed.
 * 2. `uid` is another home of the active account → switch home.
 * 3. `uid` belongs to another logged-in account → switch account (+ home).
 * 4. `uid` not found in any session → error state.
 *
 * When `hasEntityTarget` is `true`, the link also carries a room/device path:
 * on a successful switch we return `context-ready` so the caller opens the
 * entity within the now-correct home instead of landing on the home root.
 *
 * @returns the {@link DeepLinkOutcome} describing what the caller should do
 *   next. `ignored` means the string is not a `uid` deep-link.
 */
export const handleNotificationDeepLink = async (
	str: string,
	{ hasEntityTarget = false }: { hasEntityTarget?: boolean } = {},
): Promise<DeepLinkOutcome> => {
	const parsed = parseNotificationDeepLink(str);
	if (!parsed) return 'ignored';

	const { email, uid } = parsed;

	try {
		const activeEmail = normalizeEmail(get(user_data).email);

		// Not logged in at all — nothing to validate against.
		if (!activeEmail) {
			showUnresolvedHomeError();
			return 'routed';
		}

		// Outcomes 1 & 2: the notification targets the active account.
		if (!email || email === activeEmail) {
			return await routeWithinActiveAccount(uid, hasEntityTarget);
		}

		// Outcome 3 (multi-account): another logged-in account owns the uid.
		const profile = get(profiles).find((p) => normalizeEmail(p.email ?? '') === email);
		if (profile) {
			return await switchAccountAndRoute(
				{ email: profile.email ?? '', id: profile.id, sub: profile.sub },
				uid,
				hasEntityTarget,
			);
		}

		// Outcome 4: uid/email not present in any logged-in session.
		showUnresolvedHomeError();
		return 'routed';
	} catch (error) {
		console.error('[NOTIFICATION_DEEPLINK] Failed to route notification:', error);
		showUnresolvedHomeError();
		return 'routed';
	}
};
